<script lang="ts">
    import { page } from '$app/stores';
    import { getGlobals } from '$lib/global/globals';
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { paymentsApi } from '$lib/global/payments/api';
    import { themeBorderRadius, advancedLinking } from '$lib/Stores/stores';
    import { AdvancedLinkingEvaluator } from '$lib/utils/advancedLinkingEvaluator';
    import { cn } from '$lib/utils/cn';
    import { getSessionId } from '$lib/utils/initSessionId';
    import { addScrollToParam } from '$lib/utils/pageNavigation';
    import { getAllUserAnswers } from '$lib/utils/userAnswerStorage';

    import isEmpty from 'lodash/isEmpty';

    import Loading from './Loading.svelte';
    import LockIcon from './LockIcon.svelte';
    import StripeIcon from './StripeIcon.svelte';
    import { DEFAULT_LAYOUT } from '../../constants';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { PaymentFormInitSuccessResult } from '$lib/global/payments/api';
    import type { Align, Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';
    import type { Appearance, Stripe, StripeElements } from '@stripe/stripe-js';
    import type { JSONContent } from '@tiptap/core';

    export let id = '';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let firstResultPage = '';
    export let regularNextPage = '';
    export let nextPage = '';
    export let externalLink = '';
    export let isInternal = true;
    export let linkingComponentId: string | undefined = undefined;
    export let box: BoxType;
    export let align: Align = 'center';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'en';

    export let paymentConfigId: string;

    // Text
    export let draft: JSONContent;
    export let color = 'black';
    export let layout = DEFAULT_LAYOUT;

    // Button
    export let btnBgColor = '#0076FF';
    export let btnBgColorDark = '#0076FF';
    export let btnColor = 'white';
    export let btnText = '';

    const { data } = $page;
    const pageIdSlugMap = data?.pageIdSlugMap;

    const TRANSLATIONS = {
        en: {
            secured: 'Secured by',
            initFailed: 'Initialising Stripe failed.',
            unknownError: 'An unexpected error occurred.',
        },
        de: {
            secured: 'Abgesichert durch',
            initFailed: 'Initialisierung von Stripe fehlgeschlagen.',
            unknownError: 'Ein unerwarteter Fehler ist aufgetreten.',
        },
    };

    const paymentFormId = `payment-form-${id}`;
    const paymentElementId = `payment-element-${id}`;
    const paymentMessageId = `payment-message-${id}`;

    let stripe: Stripe | null;
    let elements: StripeElements;
    let errorMessage = '';
    let loading = true;
    let submitting = false;
    let stripeSetupIncomplete = false;

    const { perspectiveSessionId, globalId } = getGlobals();

    const radiusPixelMap = {
        sharp: '4px',
        small: '8px',
        medium: '12px',
        large: '16px',
    };

    // Text
    const textBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'large',
        left: 'none',
    };

    // Stripe Elements appearance
    const appearance: Appearance = {
        theme: 'stripe',
        labels: 'floating',
        variables: {
            colorPrimary: btnBgColor,
            borderRadius: radiusPixelMap[$themeBorderRadius],
        },
        rules: {
            '.Input': {
                boxShadow: 'none',
                paddingTop: '8px',
                paddingBottom: '8px',
                paddingLeft: '16px',
                paddingRight: '16px',
                borderColor: 'rgb(229 231 235)',
            },
            '.Label--resting': {
                color: 'rgb(156 163 175)',
            },
            '.Label--floating': {
                color: 'rgb(156 163 175)',
            },
            '.Input--invalid': {
                borderColor: 'rgb(209 213 219)',
                boxShadow: 'none',
            },
            '.Error': {
                color: 'rgb(239 68 68)',
                fontSize: '0.75rem',
                lineHeight: '1rem',
            },
        },
    };

    const initStripe = async (node) => {
        try {
            const initResult = await paymentsApi.paymentForm.init(paymentConfigId);

            if (initResult.status === 'stripe-setup-incomplete') {
                stripeSetupIncomplete = true;
                return;
            }

            const { publishableKey, externalAccountId }: PaymentFormInitSuccessResult = initResult;

            // Lazy-load Stripe only when component is rendered
            const { loadStripe } = await import('@stripe/stripe-js');
            stripe = await loadStripe(publishableKey, {
                stripeAccount: externalAccountId,
            });

            const clientSecret = await paymentsApi.paymentIntent.create({
                paymentConfigId,
                perspectiveSessionId,
                globalId,
            });

            elements = stripe!.elements({
                appearance,
                clientSecret,
                locale: language,
            });

            const paymentElement = elements.create('payment');

            // Remove Skeleton
            loading = false;

            // Render real payment element
            paymentElement.mount(node);
        } catch (err) {
            loading = false;
            errorMessage = TRANSLATIONS[language].initFailed;
            console.warn(`Initiating Stripe failed`, { error: err });
        }
    };

    const nextPageNavigator = new NextPageNavigator(
        campaignId,
        firstResultPage,
        pageIdSlugMap,
        trackingProperties,
    );

    // Calculate return URL based on advanced linking logic
    const calculateReturnUrl = () => {
        // Advanced linking evaluation
        if (id && !isEmpty(advancedLinking)) {
            const userAnswers = getAllUserAnswers(campaignVersionId);
            const result = AdvancedLinkingEvaluator.evaluate(id, userAnswers, $advancedLinking);

            if (result) {
                if (result.isUrl) {
                    return result.target;
                }

                // Handle scroll action - add scrollTo param to return URL
                if (result.target === 'scroll' && result.componentId) {
                    const targetPage = result.pageId || regularNextPage;
                    const baseUrl = `${window.location.origin}${nextPageNavigator.getNextPageUrl(
                        targetPage,
                    )}?sessionId=${getSessionId()}`;

                    return addScrollToParam(baseUrl, result.componentId);
                }

                // Internal page logic
                const isNext = result.target === 'next';
                const isResult = isNext && nextPage === 'result';
                const targetPage = isResult ? 'result' : isNext ? nextPage : result.target;

                return `${window.location.origin}${nextPageNavigator.getNextPageUrl(
                    targetPage,
                )}?sessionId=${getSessionId()}`;
            }
        }

        // External link from button settings
        if (!isInternal && externalLink) {
            return externalLink;
        }

        // Handle simple linking with scroll (componentId from Linking object)
        if (linkingComponentId) {
            const baseUrl = `${window.location.origin}${nextPageNavigator.getNextPageUrl(
                regularNextPage,
            )}?sessionId=${getSessionId()}`;

            return addScrollToParam(baseUrl, linkingComponentId);
        }

        // Default internal URL
        return `${window.location.origin}${nextPageNavigator.getNextPageUrl(
            regularNextPage,
        )}?sessionId=${getSessionId()}`;
    };

    const handleSubmit = async (evt: SubmitEvent) => {
        try {
            evt.preventDefault();
            submitting = true;
            errorMessage = '';
            const returnUrl = calculateReturnUrl();

            const confirmation = await stripe!.confirmPayment({
                elements,
                confirmParams: {
                    return_url: returnUrl,
                },
            });

            // This point will only be reached if there is an immediate error when
            // confirming the payment. Otherwise, your customer will be redirected to
            // your `return_url`. For some payment methods like iDEAL, your customer will
            // be redirected to an intermediate site first to authorize the payment, then
            // redirected to the `return_url`.
            if (
                confirmation?.error.type === 'card_error' ||
                confirmation?.error.type === 'validation_error'
            ) {
                errorMessage = confirmation?.error.message || '';
            } else {
                errorMessage = TRANSLATIONS[language].unknownError;
                console.warn(confirmation?.error);
            }
        } catch (err) {
            errorMessage = TRANSLATIONS[language].unknownError;
            console.warn(`Confirm Stripe payment failed`, { error: err });
        } finally {
            submitting = false;
        }
    };
</script>

<Box {id} {...getBoxAttributes({ box })} {transitionDelay} {transitionEffect} narrow>
    <Text transitionEffect="none" raw={draft} {align} {color} box={textBox} {layout} />

    <form id={paymentFormId} on:submit={handleSubmit}>
        <div id={paymentElementId} use:initStripe></div>

        {#if loading}
            <Loading {stripeSetupIncomplete} {language} />
        {/if}

        {#if errorMessage}
            <div id={paymentMessageId} class="fun-mt-4 fun-text-xs fun-text-red-500">
                {errorMessage}
            </div>
        {/if}

        <button
            type="submit"
            class={cn(
                'fun-mt-4 fun-w-full fun-bg-[var(--buttonBgColor)] fun-p-4 fun-text-center fun-font-semibold fun-transition-colors hover:fun-bg-[var(--buttonBgColorDark)]',
                getBorderRadius('button'),
            )}
            style="--buttonBgColor: {btnBgColor}; color: {btnColor}; --buttonBgColorDark: {btnBgColorDark}"
            disabled={submitting}
        >
            {#if submitting}
                <div
                    class="fun-pointer-events-none fun-flex fun-h-[21px] fun-items-center fun-justify-center"
                >
                    <span
                        class="fun-block fun-size-4 fun-animate-button-spinner fun-rounded-full fun-border-2 fun-border-gray-200 fun-border-r-transparent fun-border-t-transparent"
                    />
                </div>
            {:else}
                {btnText}
            {/if}
        </button>

        <div
            class="fun-mt-4 fun-flex fun-items-center fun-justify-center fun-gap-1.5 fun-px-4 fun-text-sm fun-text-gray-500"
        >
            <LockIcon className="fun-size-3" />
            <span>{TRANSLATIONS[language].secured}</span>
            <StripeIcon className="fun-mt-[2px]" />
        </div>
    </form>
</Box>
